'use strict';

// --- Mobile Navigation ---
const MobileNav = {
  init() {
    this.toggle = document.querySelector('.header__menu-toggle');
    this.nav = document.getElementById('mobile-nav');
    this.close = document.getElementById('mobile-nav-close');
    this.backdrop = document.getElementById('mobile-nav-backdrop');

    if (!this.toggle || !this.nav) return;

    this.toggle.addEventListener('click', () => this.open());
    this.close?.addEventListener('click', () => this.closeNav());
    this.backdrop?.addEventListener('click', () => this.closeNav());

    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.nav.classList.contains('is-open')) this.closeNav();
    };
    document.addEventListener('keydown', this.escapeHandler);
  },

  open() {
    this.nav.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.close?.focus();
  },

  closeNav() {
    this.nav.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.toggle.focus();
  }
};

// --- Sticky Header ---
const StickyHeader = {
  init() {
    this.header = document.getElementById('site-header');
    if (!this.header || !this.header.classList.contains('site-header--sticky')) return;

    this.sentinel = document.createElement('div');
    this.sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
    document.body.insertBefore(this.sentinel, document.body.firstChild);

    this.observer = new IntersectionObserver(
      ([entry]) => this.header.classList.toggle('site-header--scrolled', !entry.isIntersecting),
      { rootMargin: '-1px 0px 0px 0px', threshold: [1] }
    );
    this.observer.observe(this.sentinel);
  }
};

// --- Product Gallery ---
const ProductGallery = {
  init() {
    const mainImage = document.getElementById('product-main-image');
    if (!mainImage) return;

    const mainImg = mainImage.querySelector('img');
    const thumbnails = document.querySelectorAll('.product__thumbnail');

    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.imageSrc;
        const srcset = thumb.dataset.imageSrcset;

        if (mainImg && src) {
          mainImg.src = src;
          if (srcset) mainImg.srcset = srcset;
        }

        document.querySelector('.product__thumbnail--active')
          ?.classList.remove('product__thumbnail--active');
        thumb.classList.add('product__thumbnail--active');
      });
    });
  }
};

// --- Product Tabs ---
const ProductTabs = {
  init() {
    const tabBtns = [...document.querySelectorAll('.tabs__btn')];
    if (!tabBtns.length) return;

    const panels = {};
    tabBtns.forEach(btn => {
      panels[btn.dataset.tab] = document.getElementById(`tab-${btn.dataset.tab}`);
    });

    tabBtns.forEach((btn, i) => {
      btn.addEventListener('click', () => this.activate(btn, tabBtns, panels));

      btn.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          tabBtns[Math.min(i + 1, tabBtns.length - 1)].focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          tabBtns[Math.max(i - 1, 0)].focus();
        }
      });
    });
  },

  activate(activeBtn, tabBtns, panels) {
    tabBtns.forEach(b => {
      b.classList.remove('tabs__btn--active');
      b.setAttribute('aria-selected', 'false');
    });
    Object.values(panels).forEach(p => p?.classList.remove('tabs__content--active'));

    activeBtn.classList.add('tabs__btn--active');
    activeBtn.setAttribute('aria-selected', 'true');
    panels[activeBtn.dataset.tab]?.classList.add('tabs__content--active');
  }
};

// --- Cart Counter ---
const CartCounter = {
  async init() { await this.refresh(); },

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const counter = document.querySelector('.header__cart-count');
      if (!counter) return;
      counter.textContent = cart.item_count;
      counter.hidden = cart.item_count === 0;
    } catch {
      // cart fetch failed — silent
    }
  }
};

// --- Add to Cart (AJAX) ---
const AddToCart = {
  init() {
    document.querySelectorAll('[id="product-form"]').forEach(form => {
      form.addEventListener('submit', (e) => this.handle(e, form));
    });
  },

  async handle(e, form) {
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (!btn) return;

    const originalHTML = btn.innerHTML;
    btn.classList.add('btn--loading');
    btn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!res.ok) throw new Error('Add to cart failed');

      await CartCounter.refresh();

      btn.textContent = '✓ Ajouté — Voir le panier';
      btn.disabled = false;
      btn.classList.remove('btn--loading');
      btn.addEventListener('click', () => { window.location.href = '/cart'; }, { once: true });
    } catch {
      btn.innerHTML = originalHTML;
      btn.disabled = false;
      btn.classList.remove('btn--loading');
    }
  }
};

// --- Newsletter form feedback ---
const Newsletter = {
  init() {
    const form = document.getElementById('newsletter-form');
    if (!form) return;
    form.addEventListener('submit', () => {
      const btn = form.querySelector('button[type="submit"]');
      if (btn) { btn.textContent = 'Inscription en cours…'; btn.disabled = true; }
    });
  }
};

// --- FAQ Accordion (animated via grid-template-rows) ---
const FaqAccordion = {
  init() {
    document.querySelectorAll('.accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const answerId = trigger.getAttribute('aria-controls');
        const answer = document.getElementById(answerId);
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

        trigger.setAttribute('aria-expanded', String(!isOpen));
        answer?.classList.toggle('is-open', !isOpen);
        trigger.closest('.accordion__item')?.classList.toggle('is-open', !isOpen);
      });
    });
  }
};

// --- Scroll Reveal ---
const ScrollReveal = {
  init() {
    const els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });

    els.forEach(el => obs.observe(el));
  }
};

// --- Sticky Product Bar ---
const StickyProductBar = {
  init() {
    const bar = document.getElementById('product-sticky-bar');
    const buyBox = document.getElementById('product-form');
    if (!bar || !buyBox) return;

    const obs = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting;
      bar.classList.toggle('product__sticky-bar--visible', visible);
      bar.setAttribute('aria-hidden', String(!visible));
    }, { threshold: 0 });

    obs.observe(buyBox);
  }
};

// --- Init ---
document.addEventListener('DOMContentLoaded', () => {
  MobileNav.init();
  StickyHeader.init();
  ProductGallery.init();
  ProductTabs.init();
  CartCounter.init();
  AddToCart.init();
  Newsletter.init();
  FaqAccordion.init();
  ScrollReveal.init();
  StickyProductBar.init();
});
